import $ from 'jquery';

/**
 * Tab key aliases mapping Dutch/English slugs to possible button IDs and text titles.
 */
export const TAB_MAP = {
	doel: {
		ids: ['doel', 'goal'],
		texts: ['doel', 'goal'],
	},
	competenties: {
		ids: ['competenties', 'competencies'],
		texts: ['competenties', 'competencies'],
	},
	programma: {
		ids: ['programma', 'program'],
		texts: ['programma', 'program'],
	},
	traject: {
		ids: ['traject'],
		texts: ['traject'],
	},
	investering: {
		ids: ['investering', 'investment'],
		texts: ['investering', 'investment'],
	},
	locatie: {
		ids: ['locatie', 'location'],
		texts: ['locatie', 'location'],
	},
	trainers: {
		ids: ['trainers', 'trainer'],
		texts: ['trainers', 'trainer'],
	},
	reviews: {
		ids: ['reviews', 'testimonial', 'testimonials'],
		texts: ['reviews', 'testimonial', 'testimonials'],
	},
	'veelgestelde-vragen': {
		ids: ['veelgestelde-vragen', 'faq'],
		texts: ['veelgestelde vragen', 'faq'],
	},
};

/**
 * Activate a given tab title button and its corresponding panel.
 */
export const activateTab = ($btn, $tabs = null, $buttons = null, $panels = null) => {
	if (!$btn || !$btn.length) return;

	if (!$tabs || !$tabs.length) {
		$tabs = $btn.closest('.mi-tabs, .e-n-tabs, .elementor-tabs');
	}
	if (!$tabs.length) return;

	if (!$buttons || !$buttons.length) {
		$buttons = $tabs.find('.e-n-tab-title, .elementor-tab-title');
	}
	if (!$panels || !$panels.length) {
		$panels = $tabs.find('[role="tabpanel"], .elementor-tab-content');
	}

	// Reset all buttons & panels
	$buttons
		.attr('aria-selected', 'false')
		.attr('tabindex', '-1')
		.removeClass('e-active elementor-active');
	$panels.removeClass('e-active elementor-active').hide();

	// Set active on chosen button
	$btn.attr('aria-selected', 'true')
		.attr('tabindex', '0')
		.addClass('e-active elementor-active');

	// Show matching panel
	const tabIndex = $btn.attr('data-tab-index') || $btn.attr('data-tab');
	let $panel = null;

	if (tabIndex !== undefined && tabIndex !== null && tabIndex !== '') {
		$panel = $panels.filter(
			'[data-tab-index="' + tabIndex + '"], [data-tab="' + tabIndex + '"]',
		);
	}

	if (!$panel || !$panel.length) {
		const btnIdx = $buttons.index($btn);
		if (btnIdx !== -1) {
			$panel = $panels.eq(btnIdx);
		}
	}

	if ($panel && $panel.length) {
		$panel.addClass('e-active elementor-active').show();
	}

	// Scroll active tab button horizontally into view on mobile/tablet
	const heading = $btn.closest('.e-n-tabs-heading')[0];
	if (heading && heading.scrollWidth > heading.clientWidth) {
		const btnEl = $btn[0];
		const scrollLeft = btnEl.offsetLeft - (heading.clientWidth / 2) + (btnEl.clientWidth / 2);
		heading.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
	}

	// Trigger click event for native Elementor listeners & third-party scripts (e.g. mi-sticky)
	$btn.trigger('click');
};

/**
 * Find tab button within a container matching a target slug or tab identifier.
 */
export const findTabButton = ($tabs, targetSlug) => {
	if (!targetSlug) return null;
	const slug = targetSlug.toLowerCase().replace(/^#/, '').trim();
	if (!slug) return null;

	const $buttons = $tabs.find('.e-n-tab-title, .elementor-tab-title');
	let $matchedBtn = null;

	// 1. Direct ID match on tab title button (e.g. id="programma")
	$buttons.each(function () {
		const btnId = ($(this).attr('id') || '').toLowerCase();
		if (btnId === slug) {
			$matchedBtn = $(this);
			return false;
		}
	});
	if ($matchedBtn && $matchedBtn.length) return $matchedBtn;

	// 2. Check TAB_MAP for normalized mapping
	let mapConfig = TAB_MAP[slug];
	if (!mapConfig) {
		for (const key in TAB_MAP) {
			const config = TAB_MAP[key];
			if (
				key === slug ||
				config.ids.includes(slug) ||
				config.texts.includes(slug)
			) {
				mapConfig = config;
				break;
			}
		}
	}

	$buttons.each(function () {
		const $btn = $(this);
		const btnId = ($btn.attr('id') || '').toLowerCase();
		const btnText = $btn.text().trim().toLowerCase();

		if (mapConfig) {
			if (
				mapConfig.ids.includes(btnId) ||
				mapConfig.texts.some((t) => btnText.includes(t))
			) {
				$matchedBtn = $btn;
				return false;
			}
		} else {
			if (btnId === slug || btnText.includes(slug)) {
				$matchedBtn = $btn;
				return false;
			}
		}
	});

	if ($matchedBtn && $matchedBtn.length) return $matchedBtn;

	// 3. Match by tab panel ID (if panel has id="programma")
	const $panels = $tabs.find('[role="tabpanel"], .elementor-tab-content');
	$panels.each(function () {
		const $panel = $(this);
		const panelId = ($panel.attr('id') || '').toLowerCase();
		if (panelId === slug) {
			const tabIdx = $panel.attr('data-tab-index') || $panel.attr('data-tab');
			if (tabIdx !== undefined && tabIdx !== null && tabIdx !== '') {
				const $btn = $buttons.filter(
					'[data-tab-index="' + tabIdx + '"], [data-tab="' + tabIdx + '"]',
				);
				if ($btn.length) {
					$matchedBtn = $btn.first();
					return false;
				}
			}
		}
	});

	return $matchedBtn;
};

/**
 * Navigate to a tab matching the given slug and optionally scroll to it.
 */
export const navigateToTab = (targetSlug, shouldScroll = true) => {
	if (!targetSlug) return false;
	const slug = targetSlug.replace(/^#/, '').trim();
	if (!slug) return false;

	let navigated = false;

	$('.mi-tabs, .e-n-tabs, .elementor-tabs').each(function () {
		const $tabs = $(this);
		const $matchedBtn = findTabButton($tabs, slug);

		if ($matchedBtn && $matchedBtn.length) {
			// Don't switch if tab is explicitly hidden by miTabsHideEmpty
			if ($matchedBtn.hasClass('mi-tab-hidden') || $matchedBtn.is(':hidden')) {
				return;
			}

			activateTab($matchedBtn);
			navigated = true;

			if (shouldScroll) {
				const navH = $('.mi-navbar--main').outerHeight() || 80;
				const targetTop = $tabs.offset().top - navH - 20;
				$('html, body').stop(true, false).animate({ scrollTop: targetTop }, 400);
			}

			return false; // Stop iterating tab containers once matched
		}
	});

	return navigated;
};

/**
 * Initialize tab navigation event handlers.
 */
export const miTabsNavigation = () => {
	// Delegate click listener on document for any link or element with anchor/hash or data-tab-target
	$(document).on('click', 'a[href*="#"], [data-tab-target]', function (e) {
		const $link = $(this);

		// Skip if clicking directly on a tab title button itself to avoid circular events
		if (
			$link.hasClass('e-n-tab-title') ||
			$link.hasClass('elementor-tab-title') ||
			$link.closest('.e-n-tab-title, .elementor-tab-title').length
		) {
			return;
		}

		let targetHash = $link.attr('data-tab-target');
		if (!targetHash) {
			const href = $link.attr('href') || '';
			const hashIndex = href.indexOf('#');
			if (hashIndex !== -1) {
				targetHash = href.substring(hashIndex + 1);
			}
		}

		if (!targetHash || targetHash === '' || targetHash === '0') return;

		const success = navigateToTab(targetHash, true);
		if (success) {
			e.preventDefault();
			if (window.history && window.history.pushState) {
				window.history.pushState(null, null, '#' + targetHash);
			}
		}
	});

	// Handle initial URL hash on page load (e.g. #programma)
	const handleInitialHash = () => {
		if (window.location.hash) {
			const hash = window.location.hash.replace('#', '');
			if (hash) {
				setTimeout(() => {
					navigateToTab(hash, true);
				}, 300);
			}
		}
	};

	$(window).on('hashchange', function () {
		if (window.location.hash) {
			const hash = window.location.hash.replace('#', '');
			if (hash) {
				navigateToTab(hash, true);
			}
		}
	});

	handleInitialHash();
};

export const miTabsStickyToggle = () => {
	const updateHeaderOffset = () => {
		const $adminBar = $('#wpadminbar');
		let adminH = 0;
		if ($adminBar.length && $adminBar.is(':visible')) {
			// WP admin bar is position: absolute on mobile (<= 782px) and scrolls off top
			if (window.innerWidth > 782 && $adminBar.css('position') === 'fixed') {
				adminH = $adminBar.outerHeight();
			}
		}

		const $navbar = $(
			'.mi-navbar--main, .elementor-location-header, [data-elementor-type="header"], header.site-header, header'
		)
			.filter(':visible')
			.first();

		let navH = 80;
		if ($navbar.length) {
			const $container = $navbar.find('> .elementor-container, > .e-con').first();
			const h = $container.length && $container.outerHeight() ? $container.outerHeight() : $navbar.outerHeight();
			if (h > 0) navH = h;
		}

		let totalTop = navH + adminH;

		// Add 18px clearance offset for curved header bottom on mobile/tablet (<= 1024px)
		if (window.innerWidth <= 1024) {
			totalTop += 18;
		}

		document.documentElement.style.setProperty('--tabs-sticky-top', totalTop + 'px');
		return totalTop;
	};

	const totalStickyTop = updateHeaderOffset();
	$(window).on('resize orientationchange', updateHeaderOffset);

	$('.mi-tabs, .e-n-tabs').each(function () {
		const $container = $(this);
		const $heading = $container.hasClass('e-n-tabs-heading')
			? $container
			: $container.find('.e-n-tabs-heading').first();

		if (!$heading.length) return;

		const $tabs = $container.hasClass('e-n-tabs')
			? $container
			: $container.find('.e-n-tabs').first();

		const heading = $heading[0];

		// Ensure $heading is wrapped in .mi-tabs-heading-wrapper for sticky positioning
		let $headingWrapper = $heading.parent('.mi-tabs-heading-wrapper');
		if (!$headingWrapper.length) {
			$heading.wrap('<div class="mi-tabs-heading-wrapper"></div>');
			$headingWrapper = $heading.parent('.mi-tabs-heading-wrapper');
		}

		let $prevBtn = $headingWrapper.find('> .mi-tabs-scroll-prev');
		if (!$prevBtn.length) {
			$prevBtn = $(
				'<button type="button" class="mi-tabs-scroll-prev" aria-label="Scroll tabs left"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>'
			);
			$headingWrapper.append($prevBtn);
		}

		let $nextBtn = $headingWrapper.find('> .mi-tabs-scroll-next');
		if (!$nextBtn.length) {
			$nextBtn = $(
				'<button type="button" class="mi-tabs-scroll-next" aria-label="Scroll tabs right"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>'
			);
			$headingWrapper.append($nextBtn);
		}

		$prevBtn.on('click', function (e) {
			e.preventDefault();
			e.stopPropagation();
			$heading.animate({ scrollLeft: heading.scrollLeft - 180 }, 250);
		});

		$nextBtn.on('click', function (e) {
			e.preventDefault();
			e.stopPropagation();
			$heading.animate({ scrollLeft: heading.scrollLeft + 180 }, 250);
		});

		const checkScrollIndicators = () => {
			if (!heading) return;
			const canScrollLeft = heading.scrollLeft > 6;
			const canScrollRight = heading.scrollWidth - heading.clientWidth - heading.scrollLeft > 6;
			$prevBtn.toggleClass('is-visible', canScrollLeft);
			$nextBtn.toggleClass('is-visible', canScrollRight);
		};

		$heading.on('scroll', checkScrollIndicators);
		$(window).on('resize orientationchange', checkScrollIndicators);
		setTimeout(checkScrollIndicators, 100);

		// Prepend sentinel element to container for precise IntersectionObserver tracking
		let $sentinel = $container.find('> .mi-tabs-sentinel');
		if (!$sentinel.length) {
			$sentinel = $('<div class="mi-tabs-sentinel"></div>').css({
				position: 'absolute',
				top: '0',
				left: '0',
				width: '100%',
				height: '1px',
				pointerEvents: 'none',
				visibility: 'hidden',
				zIndex: '-1',
			});
			$container.prepend($sentinel);
		}

		if (window.IntersectionObserver) {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						const isStuck = !entry.isIntersecting && entry.boundingClientRect.top < 300;
						if (isStuck) {
							if ($tabs.length) $tabs.addClass('is-sticky');
							$heading.addClass('is-sticky');
							$headingWrapper.addClass('is-sticky');
						} else {
							if ($tabs.length) $tabs.removeClass('is-sticky');
							$heading.removeClass('is-sticky');
							$headingWrapper.removeClass('is-sticky');
						}
					});
				},
				{
					rootMargin: '-' + totalStickyTop + 'px 0px 0px 0px',
					threshold: [0],
				}
			);
			observer.observe($sentinel[0]);
		} else {
			const checkSticky = () => {
				const top = heading.getBoundingClientRect().top;
				const targetTop = parseFloat(getComputedStyle(heading).top) || totalStickyTop;
				if (top <= targetTop + 10) {
					if ($tabs.length) $tabs.addClass('is-sticky');
					$heading.addClass('is-sticky');
					$headingWrapper.addClass('is-sticky');
				} else {
					if ($tabs.length) $tabs.removeClass('is-sticky');
					$heading.removeClass('is-sticky');
					$headingWrapper.removeClass('is-sticky');
				}
			};
			$(window).on('scroll resize', checkSticky);
			checkSticky();
		}
	});
};

$(function () {
	try {
		miTabsNavigation();
		miTabsStickyToggle();
	} catch (error) {
		console.warn('mi-tabs-navigation:', error);
	}
});
